import { sleep } from '../utils/formatters';
import { logger } from './logger';
import type { Prices, Coin, CoinDetails } from '../types';
import {
  PricesSchema,
  CoinSearchResultSchema,
  CoinDetailsSchema,
  ChartDataSchema,
  validateSchema
} from '../schemas/api.schemas';
import { z } from 'zod';

// CoinGecko API Configuration
// In development: Uses VITE_COINGECKO_API_KEY for direct API calls
// In production: Uses Vercel serverless proxy (API key stored securely server-side)
const isDev = import.meta.env.DEV;
const USE_SERVERLESS_PROXY = !isDev;

// For development only - in production, API key is handled by serverless proxy
const COINGECKO_API_KEY = isDev ? (import.meta.env.VITE_COINGECKO_API_KEY as string | undefined) : undefined;
const API_BASE = COINGECKO_API_KEY
  ? 'https://pro-api.coingecko.com/api/v3'
  : 'https://api.coingecko.com/api/v3';

// Export API status for UI indicators
// In production, assume Pro API if we're using serverless proxy (key configured server-side)
export const isUsingProAPI = (): boolean => USE_SERVERLESS_PROXY || !!COINGECKO_API_KEY;
export const getAPIType = (): 'pro' | 'free' => (USE_SERVERLESS_PROXY || COINGECKO_API_KEY) ? 'pro' : 'free';

// SECURITY: Use headers instead of URL query parameters for API keys
// This prevents API keys from being logged in browser history, server logs, etc.
const getApiHeaders = (): HeadersInit => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };

  // Add API key as header (CoinGecko also supports x-cg-pro-api-key header)
  if (COINGECKO_API_KEY) {
    headers['x-cg-pro-api-key'] = COINGECKO_API_KEY;
  }

  return headers;
};

/**
 * Call CoinGecko API via Vercel serverless proxy (for production)
 * Exported for use by other services that need direct proxy access
 */
export async function callCoinGeckoProxy(endpoint: string, params: Record<string, unknown> = {}): Promise<any> {
  const response = await fetch('/api/coingecko/proxy', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ endpoint, params }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `CoinGecko proxy error: ${response.status}`);
  }

  return response.json();
}

/**
 * Make a CoinGecko API call (auto-selects proxy vs direct based on environment)
 */
export async function callCoinGeckoAPI(endpoint: string, params: Record<string, unknown> = {}): Promise<any> {
  if (USE_SERVERLESS_PROXY) {
    return callCoinGeckoProxy(endpoint, params);
  }

  // Direct call in development - use headers for API key (more secure than URL params)
  const url = new URL(`${API_BASE}${endpoint}`);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      url.searchParams.append(key, String(value));
    }
  });

  const response = await fetchWithRetry(url.toString(), { headers: getApiHeaders() }, 3, false);
  return response.json();
}

/**
 * API Rate Limiter
 * CoinGecko Free API: ~10-50 calls/minute (conservative: 30 calls/min = 1 call every 2s)
 * CoinGecko Pro API: Much higher limits, but we still use rate limiting for safety
 */

interface QueueItem {
  fn: () => Promise<any>;
  resolve: (value: any) => void;
  reject: (error: any) => void;
}

interface ErrorLogAttempt {
  attempt: number;
  status?: number;
  statusText?: string;
  error: string;
  errorType?: string;
  isNetworkError?: boolean;
  retried: boolean;
}

interface ErrorLog {
  url: string;
  attempts: ErrorLogAttempt[];
  timestamp: string;
}

export const APIRateLimiter = (() => {
  let lastCallTime = 0;
  const minInterval = 2000; // 2 seconds between each call
  const requestQueue: QueueItem[] = [];
  let isProcessing = false;

  const throttle = async (): Promise<void> => {
    const now = Date.now();
    const timeSinceLastCall = now - lastCallTime;

    if (timeSinceLastCall < minInterval) {
      const waitTime = minInterval - timeSinceLastCall;
      await sleep(waitTime);
    }

    lastCallTime = Date.now();
  };

  const enqueue = <T>(fn: () => Promise<T>): Promise<T> => {
    return new Promise((resolve, reject) => {
      requestQueue.push({ fn, resolve, reject });
      processQueue();
    });
  };

  const processQueue = async (): Promise<void> => {
    if (isProcessing || requestQueue.length === 0) return;

    isProcessing = true;

    while (requestQueue.length > 0) {
      const item = requestQueue.shift();
      if (!item) break;

      const { fn, resolve, reject } = item;
      try {
        await throttle();
        const result = await fn();
        resolve(result);
      } catch (error) {
        reject(error);
      }
    }

    isProcessing = false;
  };

  return { throttle, enqueue };
})();

/**
 * Fetch with retry logic, backoff, and rate limiting
 * @param url - URL to fetch
 * @param options - Fetch options
 * @param maxRetries - Max retry attempts (default: 3)
 * @param silent - Suppress error notifications (default: false)
 * @returns Fetch response
 */
export async function fetchWithRetry(
  url: string,
  options: RequestInit = {},
  maxRetries: number = 3,
  silent: boolean = false
): Promise<Response> {
  const errorLog: ErrorLog = {
    url: url.split('?')[0], // Hide sensitive parameters
    attempts: [],
    timestamp: new Date().toISOString()
  };

  // The API key was historically appended as a URL query parameter via an
  // `appendApiKey()` helper. That approach was replaced by header-based
  // authentication (`x-cg-pro-api-key`) in `getApiHeaders()` for security
  // (URL params leak into browser history, server logs and Sentry breadcrumbs).
  // The leftover `appendApiKey(url)` call here referenced a function that
  // no longer existed, throwing a ReferenceError on the fallback path that
  // surfaced in TokenInput tests. Use the URL as-is.
  const urlWithKey = url;

  // Merge API headers with provided options
  const mergedOptions: RequestInit = {
    ...options,
    headers: {
      ...getApiHeaders(),
      ...options.headers,
    },
  };

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      // Rate limiting to avoid API limits
      await APIRateLimiter.throttle();

      const response = await fetch(urlWithKey, mergedOptions);
      if (response.ok) {
        // Log success if it was after a retry
        if (attempt > 0) {
          console.info(`✅ API Success after ${attempt + 1} attempts:`, errorLog.url);
        }
        return response;
      }

      // If 4xx error (client), don't retry
      if (response.status >= 400 && response.status < 500) {
        const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
        errorLog.attempts.push({
          attempt: attempt + 1,
          status: response.status,
          statusText: response.statusText,
          error: error.message,
          retried: false
        });

        // Log client error (except 404 which is normal)
        if (response.status !== 404 && !silent) {
          logger.warn('API', `⚠️ API Client Error (${response.status})`, errorLog.url);
        }
        throw error;
      }

      // For 5xx errors, retry
      const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
      errorLog.attempts.push({
        attempt: attempt + 1,
        status: response.status,
        statusText: response.statusText,
        error: error.message,
        retried: attempt < maxRetries - 1
      });

      throw error;
    } catch (error) {
      const err = error as Error;
      const isNetworkError = err.name === 'TypeError' || err.message.includes('Failed to fetch');
      const isLastAttempt = attempt === maxRetries - 1;

      if (!errorLog.attempts.some(a => a.attempt === attempt + 1)) {
        errorLog.attempts.push({
          attempt: attempt + 1,
          error: err.message,
          errorType: err.name,
          isNetworkError,
          retried: !isLastAttempt
        });
      }

      if (isLastAttempt) {
        if (!silent) {
          logger.error('API', `❌ API Error (all attempts failed)`, {
            ...errorLog,
            finalError: err.message,
            errorType: err.name
          });
        }
        throw error;
      }

      // Exponential backoff: 1s, 2s, 4s...
      const backoffTime = Math.min(1000 * Math.pow(2, attempt), 8000);
      await sleep(backoffTime);
    }
  }

  // This should never be reached due to throw in loop, but TypeScript needs it
  throw new Error('Maximum retries exceeded');
}

/**
 * API Service for CoinGecko
 */
export const CoinGeckoAPI = {
  /**
   * Fetch prices for multiple coins
   */
  async fetchPrices(coinIds: string[]): Promise<Prices> {
    if (!coinIds || coinIds.length === 0) return {};

    let rawData: any;

    if (USE_SERVERLESS_PROXY) {
      rawData = await callCoinGeckoProxy('/simple/price', {
        ids: coinIds.join(','),
        vs_currencies: 'usd,eur',
        include_24hr_change: true
      });
    } else {
      const url = `${API_BASE}/simple/price?ids=${coinIds.join(',')}&vs_currencies=usd,eur&include_24hr_change=true`;
      const response = await fetchWithRetry(url, {}, 3, false);
      rawData = await response.json();
    }

    // Validate response structure
    const validatedData = validateSchema(PricesSchema, rawData, 'fetchPrices');
    if (!validatedData) {
      logger.error('API', 'Invalid prices response', { coinIds, rawData });
      return {};
    }

    return validatedData as Prices;
  },

  /**
   * Search coins
   */
  async searchCoins(query: string): Promise<{ coins: Coin[] }> {
    let rawData: any;

    if (USE_SERVERLESS_PROXY) {
      rawData = await callCoinGeckoProxy('/search', { query });
    } else {
      const url = `${API_BASE}/search?query=${encodeURIComponent(query)}`;
      const response = await fetchWithRetry(url, {}, 2, true);
      rawData = await response.json();
    }

    // Validate response structure
    const validatedData = validateSchema(CoinSearchResultSchema, rawData, `searchCoins(${query})`);
    if (!validatedData) {
      logger.error('API', 'Invalid search coins response', { query, rawData });
      return { coins: [] };
    }

    return validatedData;
  },

  /**
   * Get coin details
   */
  async getCoinDetails(coinId: string): Promise<CoinDetails> {
    let rawData: any;

    if (USE_SERVERLESS_PROXY) {
      rawData = await callCoinGeckoProxy(`/coins/${encodeURIComponent(coinId)}`, {
        localization: false,
        tickers: false,
        community_data: false,
        developer_data: false
      });
    } else {
      const url = `${API_BASE}/coins/${encodeURIComponent(coinId)}?localization=false&tickers=false&community_data=false&developer_data=false`;
      const response = await fetchWithRetry(url, {}, 3, false);
      rawData = await response.json();
    }

    // Validate response structure
    const validatedData = validateSchema(CoinDetailsSchema, rawData, `getCoinDetails(${coinId})`);
    if (!validatedData) {
      logger.error('API', 'Invalid coin details response', { coinId, rawData });
      throw new Error(`Invalid coin details response for ${coinId}`);
    }

    return validatedData;
  },

  /**
   * Get historical chart data
   */
  async getChartData(coinId: string, days: number = 7): Promise<{ prices: number[][]; market_caps: number[][]; total_volumes: number[][] }> {
    let rawData: any;

    // CoinGecko interval support:
    // - days 1: 5-minute data
    // - days 2-90: hourly data
    // - days > 90: daily data (interval param ignored)
    const useInterval = days <= 90 ? 'hourly' : undefined;

    if (USE_SERVERLESS_PROXY) {
      const params: Record<string, unknown> = {
        vs_currency: 'usd',
        days,
      };
      if (useInterval) {
        params.interval = useInterval;
      }
      rawData = await callCoinGeckoProxy(`/coins/${encodeURIComponent(coinId)}/market_chart`, params);
    } else {
      const intervalParam = useInterval ? `&interval=${useInterval}` : '';
      const url = `${API_BASE}/coins/${encodeURIComponent(coinId)}/market_chart?vs_currency=usd&days=${days}${intervalParam}`;
      const response = await fetchWithRetry(url, {}, 3, false);
      rawData = await response.json();
    }

    // Log raw response for debugging
    console.log(`[CoinGeckoAPI.getChartData] Raw response for ${coinId}:`, {
      hasPrices: !!rawData?.prices,
      pricesLength: rawData?.prices?.length || 0,
      hasMarketCaps: !!rawData?.market_caps,
      hasTotalVolumes: !!rawData?.total_volumes
    });

    // Validate response structure
    const validatedData = validateSchema(ChartDataSchema, rawData, `getChartData(${coinId})`);
    if (!validatedData) {
      logger.error('API', 'Invalid chart data response', { coinId, days, rawData });
      return { prices: [], market_caps: [], total_volumes: [] };
    }

    console.log(`[CoinGeckoAPI.getChartData] Validated ${validatedData.prices.length} price points for ${coinId}`);
    return validatedData;
  },

  /**
   * Get all coins list
   */
  async getCoinsList(): Promise<Coin[]> {
    let rawData: any;

    if (USE_SERVERLESS_PROXY) {
      rawData = await callCoinGeckoProxy('/coins/list', { include_platform: false });
    } else {
      const url = `${API_BASE}/coins/list?include_platform=false`;
      const response = await fetchWithRetry(url, {}, 3, true);
      rawData = await response.json();
    }

    // Validate response structure - array of coins
    const validatedData = validateSchema(z.array(z.object({
      id: z.string(),
      symbol: z.string(),
      name: z.string()
    })), rawData, 'getCoinsList');

    if (!validatedData) {
      logger.error('API', 'Invalid coins list response', { rawData });
      return [];
    }

    return validatedData as Coin[];
  },

  /**
   * Get all coins list with daily cache
   * Cache duration: 24 hours
   * This reduces API calls by storing the coins list in localStorage
   */
  async getCachedCoinsList(): Promise<Coin[]> {
    const CACHE_KEY = 'coingecko_coins_cache';
    const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

    try {
      // Check if we have cached data
      const cachedData = localStorage.getItem(CACHE_KEY);

      if (cachedData) {
        const { coins, timestamp } = JSON.parse(cachedData);
        const age = Date.now() - timestamp;

        // If cache is still valid (less than 24h old)
        if (age < CACHE_DURATION) {
          logger.info('API', `✅ Using cached coins list (${coins.length} coins, ${Math.floor(age / 1000 / 60 / 60)}h old)`);
          return coins;
        } else {
          logger.info('API', `⚠️ Cached coins list expired (${Math.floor(age / 1000 / 60 / 60)}h old), fetching fresh data...`);
        }
      }

      // Cache is missing or expired, fetch from API
      logger.info('API', '📡 Fetching fresh coins list from API...');
      const coins = await this.getCoinsList();

      // Store in cache with timestamp
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        coins,
        timestamp: Date.now()
      }));

      logger.info('API', `✅ Cached ${coins.length} coins for 24 hours`);
      return coins;
    } catch (error) {
      logger.error('API', 'Error fetching coins list', error);

      // Try to return expired cache as fallback
      const cachedData = localStorage.getItem(CACHE_KEY);
      if (cachedData) {
        const { coins } = JSON.parse(cachedData);
        logger.warn('API', `⚠️ API failed, using expired cache (${coins.length} coins)`);
        return coins;
      }

      throw error;
    }
  }
};
